/**
 * CROEV Quiz v3 - branching engine (brand-neutral)
 *
 * - gated steps: a block renders only when its data-gate matches the answers
 *   (key:value,value|key2:value ; "|" = AND between clauses, "," = OR inside one,
 *   key:!value = the answer must not include it)
 * - result rules: an ordered table on the root (data-result-rules, JSON) of
 *   { key, when } rows. The first row whose `when` gate passes names the result;
 *   `*` always passes. The matched key is the derived `_result`, usable in gates,
 *   and it picks both the result step and the offer block on the revealed page.
 * - derived keys: _result, _tried (yes|no from previous_solutions)
 * - placeholders: {question_id} anywhere inside [data-cq-fill]
 * - every path reaches a result and the offer
 * - loading step with a determinate ring, ticking checklist and review marquee
 * - reveal of the purchase sections only from the result's commit button
 *
 * - derived rules (data-derived-rules, JSON [{key, value, when}]): first passing row per
 *   key wins, computed before the result rules, so `_track` (from the gender question) and
 *   `_plan` (from the household question) can gate steps, pick photos (data-track on the
 *   root) and pre-select the offer. Luma port, 2026-10-03.
 *
 * Answer data stays in memory. Nothing is written to storage. Analytics are off unless the
 * container turns them on (data-analytics="on"); then six events go to window.dataLayer,
 * Shopify.analytics.publish and an optional sendBeacon webhook, with one session id per run:
 * quiz_start, step_view, answer, result, reveal, add_to_cart (the last one is fired by the
 * offer section through window.CroevQuizAnalytics.track). Schema: .croev/luma-brain-quiz/build-notes.md
 */
(function () {
  'use strict';

  var EXCLUSIVE = { none: 1, unsure: 1 };
  var BASE_TOTAL = 18;
  var PLACEHOLDER_KEYS = { tried: 'previous_solutions' };

  /* ---------- analytics: one payload shape, three transports ---------- */

  function uid() {
    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) { /* no-op */ }
    return 'cq-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function Analytics(root) {
    var ds = root.dataset;
    this.on = ds.analytics === 'on';
    this.withAnswers = ds.analyticsAnswers !== 'off';
    this.quiz = ds.analyticsQuiz || 'quiz';
    this.hook = ds.analyticsWebhook || '';
    this.ctx = {};
    this.newSession();
  }

  Analytics.prototype.newSession = function () {
    this.sid = uid();
    this.t0 = Date.now();
    this.started = false;
    this.ctx = {};
  };

  Analytics.prototype.track = function (name, data) {
    if (!this.on) return null;
    var p = {
      event: name,
      quiz_id: this.quiz,
      session_id: this.sid,
      ts: new Date().toISOString(),
      ms_since_start: Date.now() - this.t0,
      page_path: location.pathname
    };
    var k;
    for (k in this.ctx) if (Object.prototype.hasOwnProperty.call(this.ctx, k)) p[k] = this.ctx[k];
    for (k in data || {}) if (Object.prototype.hasOwnProperty.call(data, k)) p[k] = data[k];
    var full = 'croev_quiz_' + name;
    try { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: full, croev_quiz: p }); } catch (e) { /* no-op */ }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') window.Shopify.analytics.publish(full, p);
    } catch (e) { /* no-op */ }
    if (this.hook && navigator.sendBeacon) {
      try { navigator.sendBeacon(this.hook, new Blob([JSON.stringify(p)], { type: 'text/plain;charset=UTF-8' })); } catch (e) { /* no-op */ }
    }
    try { document.dispatchEvent(new CustomEvent('croev:quiz:analytics', { detail: p })); } catch (e) { /* no-op */ }
    return p;
  };

  function CroevQuiz(root) {
    this.root = root;
    this.stage = root.querySelector('[data-cq-stage]');
    this.bar = root.querySelector('[data-cq-footerbar]');
    this.track = root.querySelector('[data-cq-track]');
    this.steps = Array.prototype.slice.call(root.querySelectorAll('.cq__step'));
    this.timers = [];
    var tot = parseInt(root.dataset.totalSteps, 10);
    if (tot > 0) BASE_TOTAL = tot;
    this.rules = [];
    try { this.rules = JSON.parse(root.dataset.resultRules || '[]'); } catch (e) { this.rules = []; }
    this.drules = [];
    try { this.drules = JSON.parse(root.dataset.derivedRules || '[]'); } catch (e) { this.drules = []; }
    this.analytics = new Analytics(root);
    this.cacheTemplates();
    this.reset(true);
    this.bind();
  }

  CroevQuiz.prototype.clearTimers = function () {
    this.timers.forEach(function (t) { clearInterval(t); clearTimeout(t); });
    this.timers = [];
  };

  CroevQuiz.prototype.reset = function (silent) {
    this.answers = {};
    this.labels = {};
    this.derived = {};
    this.visited = 0;
    this.total = BASE_TOTAL;
    this.current = -1;
    this.revealed = false;
    this.busy = false;
    this.clearTimers();
    if (this.analytics) this.analytics.newSession();
    document.body.classList.remove('quiz-revealed');
    this.root.classList.remove('cq--done');
    this.root.removeAttribute('data-result');
    this.root.removeAttribute('data-track');
    var self = this;
    this.steps.forEach(function (st) {
      st.classList.remove('is-active');
      st.querySelectorAll('.cq__opt').forEach(function (o) {
        o.classList.remove('is-selected');
        o.setAttribute('aria-pressed', 'false');
      });
      var btn = st.querySelector('.cq__btn.is-loading');
      if (btn) { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); var l = btn.querySelector('[data-cq-label]'); if (l && l.dataset.orig) l.textContent = l.dataset.orig; }
      self.returnCta(st);
    });
    if (!silent) this.start();
  };

  CroevQuiz.prototype.start = function () {
    this.goTo(this.findNext(-1));
  };

  /* ---------- gating ---------- */

  CroevQuiz.prototype.valueOf = function (key) {
    if (key.charAt(0) === '_') return this.derived[key];
    return this.answers[key];
  };

  CroevQuiz.prototype.matches = function (key, wanted) {
    var have = this.valueOf(key);
    if (have == null) return false;
    var list = Array.isArray(have) ? have : [have];
    for (var i = 0; i < list.length; i++) {
      if (wanted.indexOf(list[i]) !== -1) return true;
    }
    return false;
  };

  CroevQuiz.prototype.gatePasses = function (gate) {
    if (!gate) return true;
    var clauses = gate.split('|');
    for (var i = 0; i < clauses.length; i++) {
      var c = clauses[i].trim();
      if (!c) continue;
      var idx = c.indexOf(':');
      if (idx === -1) continue;
      var key = c.slice(0, idx).trim();
      var vals = c.slice(idx + 1).split(',').map(function (v) { return v.trim(); });
      /* negation: `key:!a,!b` passes when the answer holds none of them (and the question was answered) */
      if (vals[0].charAt(0) === '!') {
        var banned = vals.map(function (v) { return v.replace(/^!/, ''); });
        if (this.valueOf(key) == null || this.matches(key, banned)) return false;
        continue;
      }
      if (!this.matches(key, vals)) return false;
    }
    return true;
  };

  CroevQuiz.prototype.findNext = function (from) {
    for (var i = from + 1; i < this.steps.length; i++) {
      var st = this.steps[i];
      if (st.dataset.role === 'branch') continue;
      if (this.gatePasses(st.dataset.gate)) return i;
    }
    return -1;
  };

  CroevQuiz.prototype.findBranch = function (type, key) {
    for (var i = 0; i < this.steps.length; i++) {
      var st = this.steps[i];
      if (st.dataset.stepType === type && st.dataset.branchKey === key) return i;
    }
    return -1;
  };

  /* ---------- derived keys ---------- */

  CroevQuiz.prototype.derive = function () {
    var tried = this.answers.previous_solutions || [];
    if (!Array.isArray(tried)) tried = [tried];
    this.derived._tried = tried.length ? (tried.length === 1 && tried[0] === 'none' ? 'no' : 'yes') : undefined;

    /* derived rules: first passing row per key wins; rows are read in order, so a later key may gate on an earlier one */
    var self = this, done = {};
    this.drules.forEach(function (d) { if (!(d.key in done)) { done[d.key] = false; self.derived[d.key] = undefined; } });
    this.drules.forEach(function (d) {
      if (done[d.key]) return;
      if (d.when === '*' || self.gatePasses(d.when)) { self.derived[d.key] = d.value; done[d.key] = true; }
    });
    if (this.derived._track) this.root.setAttribute('data-track', this.derived._track);
    else this.root.removeAttribute('data-track');

    /* result rules: first passing row wins. _result is cleared first so a rule can never gate on itself. */
    this.derived._result = undefined;
    var r;
    for (var i = 0; i < this.rules.length; i++) {
      var w = this.rules[i].when;
      if (w === '*' || this.gatePasses(w)) { r = this.rules[i].key; break; }
    }
    this.derived._result = r || this.root.dataset.resultFallback || undefined;
    if (this.derived._result) this.root.setAttribute('data-result', this.derived._result);
  };

  /* ---------- navigation ---------- */

  CroevQuiz.prototype.goTo = function (idx) {
    if (idx < 0 || idx >= this.steps.length) return;
    var prev = this.steps[this.current];
    if (prev) { prev.classList.remove('is-active'); this.returnCta(prev); }
    this.clearTimers();
    this.current = idx;
    var st = this.steps[idx];
    st.classList.add('is-active');
    this.visited += 1;
    var skip = this.root.querySelector('[data-cq-skip]');
    if (skip) skip.hidden = this.visited > 1;
    this.applyIf(st);
    this.fillReflect(st);
    this.fillTemplates(st);
    this.moveCta(st);
    this.updateProgress(false);
    window.scrollTo({ top: 0, behavior: 'auto' });
    var focusable = st.querySelector('.cq__title');
    if (focusable) { focusable.setAttribute('tabindex', '-1'); focusable.focus({ preventScroll: true }); }
    if (st.dataset.stepType === 'loading') this.runLoading(st);
    if (st.dataset.stepType === 'proof') this.runProof(st);
    var an = this.analytics;
    if (an && !an.started) { an.started = true; an.track('quiz_start', { total_steps: this.total }); }
    if (an) {
      an.track('step_view', {
        step_key: st.dataset.stepKey,
        step_type: st.dataset.stepType,
        question_id: st.dataset.questionId || null,
        step_index: idx + 1,
        path_length: this.visited,
        total_steps: this.total
      });
    }
    try {
      this.root.dispatchEvent(new CustomEvent('croev:quiz:step', { bubbles: true, detail: { step: st.dataset.stepKey, type: st.dataset.stepType } }));
    } catch (e) { /* no-op */ }
  };

  CroevQuiz.prototype.trackAnswer = function (st) {
    if (!this.analytics || !st || st.dataset.stepType !== 'question') return;
    var qid = st.dataset.questionId;
    var v = this.answers[qid];
    if (v == null) return;
    var vals = Array.isArray(v) ? v.slice() : [v];
    var data = { step_key: st.dataset.stepKey, question_id: qid, selection: st.dataset.selection || 'single', answer_count: vals.length, path_length: this.visited };
    if (this.analytics.withAnswers) data.values = vals;
    this.analytics.track('answer', data);
  };

  CroevQuiz.prototype.advance = function () {
    if (this.revealed) return;
    this.trackAnswer(this.steps[this.current]);
    var next = this.findNext(this.current);
    if (next === -1) { this.showResult(); return; }
    this.goTo(next);
  };

  CroevQuiz.prototype.showResult = function () {
    var key = this.derived._result;
    var idx = this.findBranch('result_step', key);
    if (idx === -1) idx = this.findBranch('result_step', this.root.dataset.resultFallback || '');
    if (idx === -1) return;
    var st = this.steps[idx];
    this.fillChips(st.querySelector('[data-cq-chips]'));
    this.fillBecause(st);
    this.fillProfile(st);
    this.fillAvoid(st);
    if (this.analytics) {
      this.analytics.ctx.result_key = key || null;
      this.analytics.ctx.track = this.derived._track || null;
      this.analytics.track('result', { result_key: st.dataset.branchKey, path_length: this.visited + 1 });
    }
    this.goTo(idx);
    /* the result is the end of the quiz: the track fills whatever the path length was */
    this.updateProgress(true);
  };

  CroevQuiz.prototype.chipItems = function () {
    var items = [];
    (this.labels.problem_areas || []).forEach(function (l) { items.push(l); });
    return items;
  };

  CroevQuiz.prototype.fillChips = function (box) {
    if (!box) return;
    box.innerHTML = '';
    var items = this.chipItems();
    if (this.labels.change_duration && box.dataset.withDuration != null) items.push(this.labels.change_duration);
    items.forEach(function (l) {
      var chip = document.createElement('span');
      chip.className = 'cq__chip';
      chip.textContent = l;
      box.appendChild(chip);
    });
    box.hidden = items.length === 0;
  };

  CroevQuiz.prototype.fillReflect = function (st) {
    var self = this;
    st.querySelectorAll('[data-cq-reflect]').forEach(function (line) {
      var box = line.querySelector('[data-cq-chips]');
      var src = line.dataset.cqReflectSource || 'problem_areas';
      if (src === 'problem_areas') self.fillChips(box);
      else self.fillChipsFrom(box, src);
      line.hidden = !box || box.hidden;
    });
  };

  CroevQuiz.prototype.fillChipsFrom = function (box, qid) {
    if (!box) return;
    box.innerHTML = '';
    var l = this.labels[qid];
    var items = l == null ? [] : (Array.isArray(l) ? l : [l]);
    items.forEach(function (x) {
      var chip = document.createElement('span');
      chip.className = 'cq__chip';
      chip.textContent = x;
      box.appendChild(chip);
    });
    box.hidden = items.length === 0;
  };

  CroevQuiz.prototype.fillBecause = function (st) {
    var wrap = st.querySelector('[data-cq-because]');
    if (!wrap) return;
    var box = wrap.querySelector('[data-cq-chips-priority]');
    this.fillChipsFrom(box, this.root.dataset.becauseSource || 'decision_priority');
    wrap.hidden = !box || box.hidden;
  };

  CroevQuiz.prototype.fillProfile = function (st) {
    var self = this;
    var card = st.querySelector('[data-cq-profile]');
    if (!card) return;
    var shown = 0;
    card.querySelectorAll('[data-cq-profile-row]').forEach(function (row) {
      var v = self.joinLabels(row.dataset.cqProfileRow, 'list');
      var dd = row.querySelector('dd');
      if (v && dd) { dd.textContent = v; row.hidden = false; shown += 1; }
      else row.hidden = true;
    });
    card.hidden = shown === 0;
  };

  CroevQuiz.prototype.fillAvoid = function (st) {
    var wrap = st.querySelector('[data-cq-avoid]');
    if (!wrap) return;
    var list = wrap.querySelector('[data-cq-avoid-list]');
    var asrc = this.root.dataset.avoidSource || 'avoid';
    var vals = this.answers[asrc] || [];
    var labs = this.labels[asrc] || [];
    if (!Array.isArray(vals)) { vals = [vals]; labs = [labs]; }
    var ds = this.root.dataset;
    var facts = {};
    try { facts = JSON.parse(ds.avoidFacts || '{}'); } catch (e) { facts = {}; }
    list.innerHTML = '';
    var n = 0;
    vals.forEach(function (v, i) {
      var fact = facts[v];
      if (!fact) return;
      var li = document.createElement('li');
      var b = document.createElement('strong');
      b.className = 'cq__avoid-lab';
      b.textContent = labs[i] || v;
      li.appendChild(b);
      li.appendChild(document.createTextNode(' '));
      var s = document.createElement('span');
      s.textContent = fact;
      li.appendChild(s);
      list.appendChild(li);
      n += 1;
    });
    wrap.hidden = n === 0;
  };

  /* ---------- inline conditionals: <span data-cq-if="gate"> shows only when the gate passes ----------
     A paragraph marked data-cq-cond hides when nothing visible is left in it. Spacing lives inside the spans. */

  CroevQuiz.prototype.applyIf = function (scope) {
    var self = this;
    (scope || this.root).querySelectorAll('[data-cq-if]').forEach(function (el) {
      el.hidden = !self.gatePasses(el.getAttribute('data-cq-if'));
    });
    (scope || this.root).querySelectorAll('[data-cq-cond]').forEach(function (p) {
      var visible = '';
      p.childNodes.forEach(function (n) {
        if (n.nodeType === 3) visible += n.textContent;
        else if (n.nodeType === 1 && !n.hidden) visible += n.textContent;
      });
      p.hidden = !visible.trim();
    });
  };

  /* ---------- placeholders: {problem_areas} {duration} {priority} {tried} {moment} {fit} ---------- */

  CroevQuiz.prototype.cacheTemplates = function () {
    document.querySelectorAll('[data-cq-fill]').forEach(function (el) {
      if (el.__cqTpl == null) el.__cqTpl = el.textContent;
    });
  };

  CroevQuiz.prototype.joinLabels = function (qid, inline) {
    var l = this.labels[qid];
    if (l == null) return '';
    var arr = Array.isArray(l) ? l.slice() : [l];
    arr = arr.map(function (x) {
      var s = String(x).replace(/\.$/, '');
      if (inline === true && !/^I(\b|')/.test(s)) s = s.charAt(0).toLowerCase() + s.slice(1);
      return s;
    });
    if (arr.length <= 1) return arr.join('');
    /* a label with its own comma ("keys, glasses or a phone") makes a comma list ambiguous: use semicolons */
    var sep = arr.some(function (x) { return x.indexOf(',') !== -1; }) ? '; ' : ', ';
    if (inline === 'list') return arr.join(sep);
    if (arr.length === 2) return arr[0] + ' and ' + arr[1];
    return arr.slice(0, -1).join(sep) + sep + 'and ' + arr[arr.length - 1];
  };

  CroevQuiz.prototype.fillTemplates = function (scope) {
    var self = this;
    var esc = function (s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); };
    (scope || document).querySelectorAll('[data-cq-fill]').forEach(function (el) {
      var tpl = el.__cqTpl != null ? el.__cqTpl : el.textContent;
      if (tpl.indexOf('{') === -1) return;
      var html = esc(tpl).replace(/\{(\w+)\}/g, function (m, key) {
        var qid = PLACEHOLDER_KEYS[key] || (self.labels[key] != null ? key : null);
        if (!qid) return m;
        var v = self.joinLabels(qid, true);
        if (!v) return '';
        return '<strong class="cq__hl">' + esc(v) + '</strong>';
      });
      el.innerHTML = html.replace(/\s{2,}/g, ' ');
    });
  };

  /* ---------- proof step: bars fill and numbers count up on entry ---------- */

  CroevQuiz.prototype.runProof = function (st) {
    var self = this;
    var duration = 1100;
    var bars = Array.prototype.slice.call(st.querySelectorAll('[data-cq-bar]'));
    var counts = Array.prototype.slice.call(st.querySelectorAll('[data-cq-count]'));
    var people = Array.prototype.slice.call(st.querySelectorAll('.cq__person.is-on'));
    bars.forEach(function (b) { b.style.width = '0%'; });
    people.forEach(function (p) { p.classList.remove('is-lit'); });
    var start = Date.now();
    var ease = function (x) { return 1 - Math.pow(1 - x, 3); };
    var timer = setInterval(function () {
      var p = Math.min(1, (Date.now() - start) / duration);
      var e = ease(p);
      bars.forEach(function (b) { b.style.width = (parseFloat(b.style.getPropertyValue('--v')) * e) + '%'; });
      counts.forEach(function (c) { c.textContent = Math.round(parseFloat(c.dataset.to) * e) + (c.dataset.suffix || ''); });
      people.forEach(function (ph, i) { if (e >= (i + 1) / (people.length + 0.2)) ph.classList.add('is-lit'); });
      if (p >= 1) { clearInterval(timer); people.forEach(function (ph) { ph.classList.add('is-lit'); }); }
    }, 40);
    this.timers.push(timer);
  };

  /* ---------- loading step: determinate ring, ticking checklist ---------- */

  CroevQuiz.prototype.runLoading = function (st) {
    var self = this;
    var duration = parseInt(st.dataset.duration, 10) || 4000;
    var ring = st.querySelector('[data-cq-ring]');
    var pctEl = st.querySelector('[data-cq-pct]');
    var items = Array.prototype.slice.call(st.querySelectorAll('[data-cq-check]'));
    var circ = ring ? parseFloat(ring.dataset.circ || '0') : 0;
    var start = Date.now();
    items.forEach(function (i) { i.classList.remove('is-done', 'is-active'); });
    if (ring) ring.style.strokeDashoffset = circ;
    var timer = setInterval(function () {
      var p = Math.min(1, (Date.now() - start) / duration);
      if (ring) ring.style.strokeDashoffset = circ * (1 - p);
      if (pctEl) pctEl.textContent = Math.round(p * 100) + '%';
      /* one item per share of the ring: in progress during its share, done when the ring passes it */
      items.forEach(function (it, i) {
        var n = items.length;
        it.classList.toggle('is-done', p >= (i + 1) / n);
        it.classList.toggle('is-active', p >= i / n && p < (i + 1) / n);
      });
      if (p >= 1) {
        clearInterval(timer);
        items.forEach(function (it) { it.classList.add('is-done'); it.classList.remove('is-active'); });
        var t = setTimeout(function () { self.advance(); }, 450);
        self.timers.push(t);
      }
    }, 50);
    this.timers.push(timer);
  };

  /* ---------- commit + reveal ---------- */

  CroevQuiz.prototype.commit = function (btn) {
    if (this.revealed || this.busy) return;
    this.busy = true;
    var self = this;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    var label = btn.querySelector('[data-cq-label]');
    var wait = btn.dataset.waitText || 'Preparing your routine…';
    if (label) { label.dataset.orig = label.textContent; label.textContent = wait; }
    var t = setTimeout(function () {
      self.revealed = true;
      self.updateProgress(true);
      if (self.analytics) self.analytics.track('reveal', { result_key: self.derived._result || null, plan: self.derived._plan || null, path_length: self.visited });
      self.showOffer();
      self.fillTemplates(document);
      document.body.classList.add('quiz-revealed');
      self.root.classList.add('cq--done');
      self.returnCta(self.steps[self.current]);
      window.scrollTo({ top: 0, behavior: 'auto' });
      self.busy = false;
    }, 1000);
    this.timers.push(t);
  };

  /* ---------- offer: one block per result on the revealed page ---------- */

  CroevQuiz.prototype.showOffer = function () {
    var key = this.derived._result;
    var blocks = Array.prototype.slice.call(document.querySelectorAll('[data-cq-offer]'));
    var hitKey = null;
    blocks.forEach(function (b) { if (b.dataset.cqOffer === key) hitKey = key; });
    if (hitKey == null && blocks.length) hitKey = blocks[0].dataset.cqOffer;
    /* every element tagged with the matched key shows (an offer may carry an intro and a why-band per result) */
    blocks.forEach(function (b) { b.hidden = b.dataset.cqOffer !== hitKey; });
    var detail = { result: hitKey || key || null, plan: this.derived._plan || null, track: this.derived._track || null };
    try {
      document.dispatchEvent(new CustomEvent('croev:quiz:offer', { detail: detail }));
    } catch (e) { /* no-op */ }
  };

  /* ---------- CTA relocation (fixed bar lives outside the transformed stage) ---------- */

  CroevQuiz.prototype.moveCta = function (st) {
    if (!this.bar) return;
    var wrap = st.querySelector('.cq__cta-wrap');
    this.bar.innerHTML = '';
    if (!wrap) { this.bar.hidden = true; return; }
    wrap.__cqHome = st;
    this.bar.appendChild(wrap);
    this.bar.hidden = false;
    this.syncContinue(st);
  };

  CroevQuiz.prototype.returnCta = function (st) {
    if (!this.bar || !st) return;
    var wrap = this.bar.querySelector('.cq__cta-wrap');
    if (wrap && wrap.__cqHome === st) {
      var home = st.querySelector('[data-cq-cta-home]') || st;
      home.appendChild(wrap);
      wrap.__cqHome = null;
    }
    this.bar.hidden = true;
  };

  CroevQuiz.prototype.syncContinue = function (st) {
    var btn = this.bar ? this.bar.querySelector('[data-action="continue"]') : null;
    if (!btn) return;
    if (st.dataset.stepType !== 'question') { btn.disabled = false; return; }
    btn.disabled = st.querySelectorAll('.cq__opt.is-selected').length === 0;
  };

  /* ---------- progress ---------- */

  CroevQuiz.prototype.updateProgress = function (done) {
    if (!this.track) return;
    var pct = done ? 100 : Math.min(100, Math.round((this.visited / this.total) * 100));
    this.track.style.transform = 'scaleX(' + (pct / 100) + ')';
    var pb = this.root.querySelector('.cq__progress');
    if (pb) { pb.setAttribute('aria-valuenow', pct); pb.style.setProperty('--cq-p', pct / 100); }
    this.updateSlider(pct, done);
    var eta = this.root.querySelector('[data-cq-eta]');
    if (eta) {
      var n = Math.min(this.visited, this.total);
      var left = Math.max(0, this.total - n) * 7;
      var t = left >= 60 ? 'about ' + Math.ceil(left / 60) + ' min left' : left > 0 ? left + ' sec left' : 'Your plan is ready';
      eta.textContent = done || left === 0 ? (this.root.dataset.doneText || 'Your routine is ready') : 'Step ' + n + ' of ' + this.total + ' · ' + t;
    }
  };

  /* slider progress (progress_style = slider): one tick per step, a thumb that rides the fill and
     bumps on every step, and a percentage that counts up. All optional: the elements only exist
     when the container renders them. */
  CroevQuiz.prototype.updateSlider = function (pct, done) {
    var self = this;
    var ticks = this.root.querySelector('[data-cq-ticks]');
    if (ticks) {
      if (ticks.children.length !== this.total + 1) {
        ticks.innerHTML = '';
        for (var i = 0; i <= this.total; i++) ticks.appendChild(document.createElement('i'));
      }
      var reached = done ? this.total : Math.min(this.visited, this.total);
      Array.prototype.forEach.call(ticks.children, function (el, i) { el.classList.toggle('is-done', i <= reached); });
    }
    var thumb = this.root.querySelector('[data-cq-thumb]');
    if (thumb) {
      thumb.classList.remove('is-bump');
      void thumb.offsetWidth;
      thumb.classList.add('is-bump');
      thumb.classList.toggle('is-done', !!done);
    }
    var out = this.root.querySelector('[data-cq-progress-pct]');
    if (out) {
      var from = parseInt(out.textContent, 10) || 0;
      if (this.pctTimer) clearInterval(this.pctTimer);
      if (from === pct) { out.textContent = pct + '%'; return; }
      var start = Date.now();
      this.pctTimer = setInterval(function () {
        var k = Math.min(1, (Date.now() - start) / 550);
        var e = 1 - Math.pow(1 - k, 3);
        out.textContent = Math.round(from + (pct - from) * e) + '%';
        if (k >= 1) { clearInterval(self.pctTimer); self.pctTimer = null; }
      }, 30);
    }
  };

  /* ---------- answers ---------- */

  CroevQuiz.prototype.selectOption = function (opt, st) {
    var qid = st.dataset.questionId;
    var multi = st.dataset.selection === 'multi';
    var opts = Array.prototype.slice.call(st.querySelectorAll('.cq__opt'));
    var val = opt.dataset.value;

    if (!multi) {
      if (this.busy) return;
      opts.forEach(function (o) { o.classList.remove('is-selected'); o.setAttribute('aria-pressed', 'false'); });
      opt.classList.add('is-selected');
      opt.setAttribute('aria-pressed', 'true');
      this.answers[qid] = val;
      this.labels[qid] = opt.dataset.label;
      this.derive();
      var self = this;
      this.busy = true;
      var t = setTimeout(function () { self.busy = false; self.advance(); }, 220);
      this.timers.push(t);
      return;
    }

    var on = !opt.classList.contains('is-selected');
    if (on && EXCLUSIVE[val]) {
      opts.forEach(function (o) { o.classList.remove('is-selected'); o.setAttribute('aria-pressed', 'false'); });
    } else if (on) {
      opts.forEach(function (o) {
        if (EXCLUSIVE[o.dataset.value]) { o.classList.remove('is-selected'); o.setAttribute('aria-pressed', 'false'); }
      });
    }
    opt.classList.toggle('is-selected', on);
    opt.setAttribute('aria-pressed', on ? 'true' : 'false');
    var vals = [], labs = [];
    opts.forEach(function (o) {
      if (o.classList.contains('is-selected')) { vals.push(o.dataset.value); labs.push(o.dataset.label); }
    });
    if (vals.length) { this.answers[qid] = vals; this.labels[qid] = labs; }
    else { delete this.answers[qid]; delete this.labels[qid]; }
    this.derive();
    this.syncContinue(st);
  };

  /* ---------- events: one delegated listener on the root ---------- */

  CroevQuiz.prototype.bind = function () {
    var self = this;
    this.root.addEventListener('click', function (e) {
      var opt = e.target.closest('.cq__opt');
      if (opt && self.root.contains(opt)) {
        var st = opt.closest('.cq__step');
        if (st && st.classList.contains('is-active')) self.selectOption(opt, st);
        return;
      }
      var btn = e.target.closest('[data-action]');
      if (!btn || !self.root.contains(btn)) return;
      var action = btn.dataset.action;
      var active = self.steps[self.current];
      if (!active) return;
      if (action === 'continue') {
        if (btn.disabled) return;
        if (active.dataset.stepType === 'question' && !self.answers[active.dataset.questionId]) return;
        self.advance();
      } else if (action === 'commit') {
        self.commit(btn);
      }
    });

    this.root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var opt = e.target.closest('.cq__opt');
      if (opt) { e.preventDefault(); opt.click(); }
    });

    window.addEventListener('pageshow', function (e) {
      if (e.persisted) self.reset(false);
    });
  };

  function init() {
    document.querySelectorAll('[data-cq-root]').forEach(function (r) {
      if (r.__cq) return;
      r.__cq = new CroevQuiz(r);
      r.__cq.start();
    });
  }

  /* public hooks for the offer section: analytics with the run's session id, and the derived keys */
  window.CroevQuizAnalytics = {
    track: function (name, data) {
      var r = document.querySelector('[data-cq-root]');
      return r && r.__cq && r.__cq.analytics ? r.__cq.analytics.track(name, data) : null;
    }
  };
  window.CroevQuizState = function () {
    var r = document.querySelector('[data-cq-root]');
    return r && r.__cq ? { result: r.__cq.derived._result || null, plan: r.__cq.derived._plan || null, track: r.__cq.derived._track || null, revealed: !!r.__cq.revealed } : null;
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', init);
})();
